import logger from '@utils/logger'
/**
 * Enhanced API Service for Dashboard
 * Secure authentication with HTTP-only cookies and role-based features
 */

import axios from 'axios'
import { createAuthRefreshCoordinator } from '@x3algo/shared/browser/authRefreshCoordinator'
import { authAttempts, authAttemptError } from '@utils/authAttemptState'

// Create axios instance with default config
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  withCredentials: true, // Essential for HTTP-only cookies
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

const privateAuthError = (error) => {
  const status = error.response?.status
  const messages = {
    400: 'Check the entered details and try again.',
    401: 'Authentication failed. Check your credentials or sign in again.',
    403: 'This security change is not available for your account.',
    409: 'The security request changed or expired. Restart this step.',
    429: 'Too many attempts. Please wait before trying again.'
  }
  const safe = new Error(
    messages[status] ||
      'Unable to confirm the security change. Check your account status before retrying.'
  )
  safe.userMessage = safe.message
  safe.status = status
  safe.privateAuthFailure = true
  return safe
}

const privateAuthOptions = { privateAuthRequest: true }
const securityResult = (response) => {
  if (response.data?.success === false) throw privateAuthError({})
  return response.data?.data ?? response.data
}

// CSRF: read XSRF-TOKEN cookie and attach as header on every mutating request
api.interceptors.request.use(config => {
  const methods = ['post', 'put', 'patch', 'delete']
  if (methods.includes(config.method)) {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
    if (match) config.headers['X-XSRF-TOKEN'] = decodeURIComponent(match[1])
  }
  return config
})

const authCommandPaths = new Set([
  '/auth/signup',
  '/auth/login',
  '/auth/2fa/login-verify',
  '/auth/refresh-token',
  '/auth/logout',
  '/auth/2fa/setup',
  '/auth/2fa/verify',
  '/auth/2fa/disable',
  '/auth/2fa/cancel',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/user/password',
  '/user/change-password'
])
const requestPath = url =>
  String(url || '')
    .split('?')[0]
    .replace(/^\/api(?=\/)/, '')
const isAuthCommand = (url, method) => {
  const path = requestPath(url)
  if (path === '/user/sessions') return method?.toLowerCase() === 'delete'
  return authCommandPaths.has(path)
}

const getCsrfToken = () => {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

async function ensureCsrfToken(signal) {
  if (getCsrfToken()) return
  await api.get('/csrf-token', {
    signal,
    skipAuthRefresh: true,
    skipMockAuth: true
  })
  if (!getCsrfToken())
    throw new Error('Unable to initialize request protection.')
}

// Request interceptor
api.interceptors.request.use(
  config => {
    config.authAttempt = config.authAttempt || authAttempts.capture()
    if (isAuthCommand(config.url, config.method))
      authAttempts.assertCurrent(config.authAttempt)
    // Add request ID for tracing
    config.headers['X-Request-ID'] = generateRequestId()

    return config
  },
  error => {
    if (error.privateAuthFailure) return Promise.reject(error)
    if (error.config?.privateAuthRequest) {
      return Promise.reject(privateAuthError(error))
    }
    logger.error('❌ API Error:', error.config?.url || 'unknown')
    return Promise.reject(error)
  }
)

// Response interceptor with automatic token refresh
api.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    if (error.privateAuthFailure) return Promise.reject(error)
    const originalRequest = error.config || {}
    // Fresh credential proofs must never be replayed or retained by retry UI.
    if (originalRequest?.privateAuthRequest) {
      return Promise.reject(privateAuthError(error))
    }
    if (isAuthCommand(originalRequest.url, originalRequest.method))
      return Promise.reject(error)
    const responseCode =
      error.response?.data?.code || error.response?.data?.error?.code

    if (responseCode === 'ADMIN_ELEVATION_REQUIRED') {
      window.dispatchEvent(
        new CustomEvent('admin:elevation-required', {
          detail: {
            requestUrl: originalRequest?.url
          }
        })
      )
    }

    // Handle 401 Unauthorized errors
    if (
      error.response?.status === 401 &&
      !originalRequest._productAuthRetried &&
      !originalRequest.skipAuthRefresh &&
      !originalRequest?.skipDashboardAuth
    ) {
      const attempt = originalRequest.authAttempt || authAttempts.capture()
      if (!authAttempts.isCurrent(attempt))
        return Promise.reject(authAttemptError())
      originalRequest._productAuthRetried = true

      try {
        await refreshAuthToken(attempt)
        authAttempts.assertCurrent(attempt)
        return api(originalRequest)
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors - extract proper error messages from API response
    if (error.response?.status === 403) {
      // Extract the actual error message from the API response
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Access denied. You may not have permission for this action.'

      // Enhance the error object with the extracted message
      error.message = errorMessage
      error.userMessage = errorMessage

      // Dedicated UI states handle account activation and admin elevation.
      if (
        !['ACCOUNT_NOT_ACTIVE', 'ADMIN_ELEVATION_REQUIRED'].includes(
          responseCode
        )
      ) {
        showErrorNotification(errorMessage)
      }
      logger.error('🚫 Access denied:', errorMessage)
    } else if (error.response?.status === 429) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Too many requests. Please slow down.'
      logger.error('⏳ Rate limit exceeded:', errorMessage)
      error.message = errorMessage
      error.userMessage = errorMessage
      showErrorNotification(errorMessage)
    } else if (error.response?.status >= 500) {
      // Server errors — show retry notification
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        `Server error (${error.response.status}). Please try again.`
      error.message = errorMessage
      error.userMessage = errorMessage
      if (!isAuthCommand(originalRequest.url, originalRequest.method))
        showRetryNotification(errorMessage, originalRequest)
    } else if (error.response?.status >= 400) {
      // Handle all other HTTP errors by extracting the message
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        `Request failed with status code ${error.response.status}`
      error.message = errorMessage
      error.userMessage = errorMessage
    } else if (!error.response) {
      const isAborted =
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError' ||
        error.message === 'canceled' ||
        error.config?.signal?.aborted
      if (isAborted) {
        return Promise.reject(error)
      }
      logger.error('🌐 Network error:', error.config?.url)
      error.message = 'Network error. Please check your internet connection.'
      error.userMessage = error.message
      if (!isAuthCommand(originalRequest.url, originalRequest.method))
        showRetryNotification(error.message, originalRequest)
    }

    return Promise.reject(error)
  }
)

/**
 * Handle authentication failure
 */
const handleAuthenticationFailure = () => {
  authAttempts.requireReauthentication()
  localStorage.removeItem('user')
  localStorage.removeItem('mockToken')
  localStorage.removeItem('tokenMetadata')
  localStorage.removeItem('notification_preferences')
  localStorage.removeItem('fcm_token')
  window.dispatchEvent(new CustomEvent('auth:logout'))

  // Redirect to landing page login
  const loginUrl =
    import.meta.env.VITE_LOGIN_URL || 'http://localhost:3000/login'
  window.location.href = `${loginUrl}?redirect=${encodeURIComponent(window.location.href)}`
}

const refreshCoordinator = createAuthRefreshCoordinator({
  state: authAttempts,
  async requestRefresh(attempt, signal) {
    await ensureCsrfToken(signal)
    authAttempts.assertCurrent(attempt)
    try {
      return (
        await api.post(
          '/auth/refresh-token',
          {},
          {
            signal,
            authAttempt: attempt,
            skipAuthRefresh: true,
            skipMockAuth: true
          }
        )
      ).data
    } catch (error) {
      const status = error.response?.status || error.status
      const code = error.response?.data?.code || error.code
      if (status === 401 || code === 'PRODUCT_SESSION_REJECTED') {
        const reauthentication = new Error('Sign in again to continue.')
        reauthentication.code = 'AUTH_REAUTHENTICATION_REQUIRED'
        reauthentication.requiresReauthentication = true
        reauthentication.privateAuthFailure = true
        reauthentication.status = 401
        throw reauthentication
      }
      throw error
    }
  },
  async probeCurrentUser(attempt, signal) {
    return (
      await api.get('/user/me', {
        signal,
        authAttempt: attempt,
        skipAuthRefresh: true,
        skipMockAuth: true
      })
    ).data
  },
  onRefreshed(result, attempt) {
    if (!authAttempts.isCurrent(attempt)) return
    if (result.tokenMetadata) storeTokenMetadata(result.tokenMetadata)
    window.dispatchEvent(
      new CustomEvent('token:refreshed', {
        detail: {
          ...(result.tokenMetadata
            ? { tokenMetadata: result.tokenMetadata }
            : {}),
          ...(result.data?.authSession
            ? { authSession: result.data.authSession }
            : {})
        }
      })
    )
  }
})

export const refreshAuthToken = async (attempt = authAttempts.capture()) => {
  try {
    return await refreshCoordinator.refresh(attempt)
  } catch (error) {
    if (
      error.requiresReauthentication ||
      error.code === 'AUTH_REAUTHENTICATION_REQUIRED'
    ) {
      handleAuthenticationFailure()
    }
    throw error
  }
}

/**
 * Store token metadata
 */
const storeTokenMetadata = tokenMetadata => {
  if (tokenMetadata) {
    try {
      localStorage.setItem('tokenMetadata', JSON.stringify(tokenMetadata))
      if (import.meta.env.DEV) {
        logger.info(
          '📅 Token expiry stored:',
          new Date(tokenMetadata.accessTokenExpiresAt).toLocaleTimeString(
            'en-IN',
            { timeZone: 'Asia/Kolkata' }
          )
        )
      }
    } catch (error) {
      if (import.meta.env.DEV)
        logger.warn('Failed to store token metadata:', error)
    }
  }
}

/**
 * Show error notification
 */
const showErrorNotification = message => {
  logger.error('Notification:', message)
  // Dispatch custom event for notification system
  window.dispatchEvent(
    new CustomEvent('show:notification', {
      detail: { type: 'error', message }
    })
  )
}

/**
 * Show error notification with retry action for 5xx / network errors
 */
const showRetryNotification = (message, failedConfig) => {
  logger.error('Retry notification:', message)
  window.dispatchEvent(
    new CustomEvent('show:notification', {
      detail: {
        type: 'error',
        message,
        retry: failedConfig ? () => api(failedConfig) : null
      }
    })
  )
}

/**
 * Generate unique request ID
 */
const generateRequestId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

/**
 * Dashboard Authentication Service
 */
export const authService = {
  /**
   * Get current authenticated user
   */
  async getCurrentUserSnapshot(
    attempt = authAttempts.capture(),
    { skipAuthRefresh = false } = {}
  ) {
    try {
      const response = await api.get('/user/me', {
        authAttempt: attempt,
        skipAuthRefresh
      })
      authAttempts.assertCurrent(attempt)

      if (response.data.success && response.data.user) {
        authAttempts.acceptCurrentSession(
          attempt,
          response.data.data?.authSession
        )
        authAttempts.assertCurrent(attempt)
        localStorage.setItem('user', JSON.stringify(response.data.user))

        // Store token metadata if present
        if (response.data.tokenMetadata) {
          storeTokenMetadata(response.data.tokenMetadata)
        }

        return {
          user: response.data.user,
          authSession: response.data.data?.authSession
        }
      }

      return null
    } catch (error) {
      if (authAttempts.isCurrent(attempt) && error.response?.status === 401) {
        localStorage.removeItem('user')
        localStorage.removeItem('tokenMetadata')
      }
      throw error
    }
  },

  async getCurrentUser(attempt = authAttempts.capture()) {
    try {
      return (await this.getCurrentUserSnapshot(attempt))?.user || null
    } catch (_error) {
      return null
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(profileData, attempt = authAttempts.capture()) {
    try {
      const response = await api.put('/user/profile', profileData, {
        authAttempt: attempt
      })
      authAttempts.assertCurrent(attempt)

      if (response.data.success && response.data.user) {
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(response.data.user))
        return {
          success: true,
          user: response.data.user,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Profile update failed'
      }
    } catch (error) {
      if (!authAttempts.isCurrent(attempt))
        return { success: false, stale: true }
      return {
        success: false,
        message: error.response?.data?.message || 'Profile update failed'
      }
    }
  },

  /**
   * Change password
   */
  async changePassword(passwordData) {
    const attempt = authAttempts.capture()
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const response = await api.put('/user/password', passwordData, {
        ...privateAuthOptions,
        authAttempt: attempt,
        skipAuthRefresh: true,
        skipMockAuth: true
      })

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        }
      }

      return {
        success: false,
        message: response.data.message || 'Password change failed'
      }
    } catch (error) {
      if (error.privateAuthFailure) throw error
      return {
        success: false,
        message: error.response?.data?.message || 'Password change failed',
        suggestions: error.response?.data?.suggestions
      }
    }
  },

  /**
   * Delete user account
   */
  async deleteAccount(password) {
    const attempt = authAttempts.capture()
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const response = await api.delete('/user/account', {
        data: { password },
        ...privateAuthOptions,
        authAttempt: attempt,
        skipAuthRefresh: true,
        skipMockAuth: true
      })
      return { success: response.data.success, message: response.data.message }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Account deletion failed'
      }
    }
  },

  /**
   * Logout user
   */
  async logout(attempt = authAttempts.capture()) {
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const response = await api.post(
        '/auth/logout',
        {},
        {
          authAttempt: attempt,
          skipAuthRefresh: true,
          skipMockAuth: true
        }
      )
      return {
        localSignedOut: true,
        serverRevoked: response.data?.data?.serverRevoked === true,
        alreadyRevoked: response.data?.data?.alreadyRevoked === true
      }
    } catch {
      return { localSignedOut: true, serverRevoked: false }
    }
  },

  /**
   * Upload avatar image
   */
  async uploadAvatar(file, attempt = authAttempts.capture()) {
    try {
      const formData = new FormData()
      formData.append('avatar', file)

      const response = await api.post('/upload/avatar', formData, {
        authAttempt: attempt,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      authAttempts.assertCurrent(attempt)

      if (response.data.success) {
        const avatarUrl = response.data.data?.avatarUrl
        const updatedUser = response.data.data?.user

        // Update stored user data with new avatar
        if (updatedUser) {
          localStorage.setItem('user', JSON.stringify(updatedUser))
        }

        return {
          success: true,
          message: response.data.message,
          data: {
            avatarUrl,
            user: updatedUser
          }
        }
      }

      return {
        success: false,
        message: response.data.message || 'Avatar upload failed'
      }
    } catch (error) {
      logger.error('❌ Upload avatar error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Avatar upload failed'
      }
    }
  },

  /**
   * Delete avatar image
   */
  async deleteAvatar(attempt = authAttempts.capture()) {
    try {
      const response = await api.delete('/upload/avatar', {
        authAttempt: attempt
      })
      authAttempts.assertCurrent(attempt)

      if (response.data.success) {
        // Update stored user data to remove avatar
        const storedUser = localStorage.getItem('user')
        if (storedUser) {
          const user = JSON.parse(storedUser)
          user.avatar = null
          localStorage.setItem('user', JSON.stringify(user))
        }

        return {
          success: true,
          message: response.data.message || 'Avatar deleted successfully'
        }
      }

      return {
        success: false,
        message: response.data.message || 'Avatar deletion failed'
      }
    } catch (error) {
      logger.error('❌ Delete avatar error:', error)
      return {
        success: false,
        message: error.response?.data?.message || 'Avatar deletion failed'
      }
    }
  },

  /**
   * Setup 2FA - get QR code and backup codes
   */
  async setup2FA(payload) {
    return securityResult(
      await api.post('/auth/2fa/setup', payload, privateAuthOptions)
    )
  },

  /**
   * Verify 2FA setup with authenticator code
   */
  async verify2FA(code, setupToken) {
    const payload = setupToken ? { code, setupToken } : { code }
    return securityResult(
      await api.post('/auth/2fa/verify', payload, privateAuthOptions)
    )
  },

  /**
   * Disable 2FA
   */
  async disable2FA(password, code, options = {}) {
    const payload = { password }
    if (options.backupCode) payload.backupCode = options.backupCode
    else payload.code = code
    if (options.operationId) payload.operationId = options.operationId
    return securityResult(
      await api.post('/auth/2fa/disable', payload, privateAuthOptions)
    )
  },

  async cancel2FA(setupToken) {
    return securityResult(
      await api.post('/auth/2fa/cancel', { setupToken }, privateAuthOptions)
    )
  },

  requireReauthentication() {
    handleAuthenticationFailure()
  },

  /**
   * Get 2FA status
   */
  async get2FAStatus() {
    return securityResult(await api.get('/auth/2fa/status', privateAuthOptions))
  },

  /**
   * Verify 2FA code during login
   */
  async verifyLogin2FA(
    tempToken,
    code,
    isBackupCode = false,
    attempt = authAttempts.capture()
  ) {
    try {
      await ensureCsrfToken()
      authAttempts.assertCurrent(attempt)
      const body = { tempToken }
      if (isBackupCode) {
        body.backupCode = code
      } else {
        body.totpCode = code
      }
      const response = await api.post('/auth/2fa/login-verify', body, {
        authAttempt: attempt,
        skipAuthRefresh: true,
        skipMockAuth: true
      })
      return {
        success: response.data.success,
        user: response.data.user,
        authSession: response.data.data?.authSession,
        tokenMetadata: response.data.tokenMetadata,
        message: response.data.message
      }
    } catch (error) {
      throw error.privateAuthFailure ? error : privateAuthError(error)
    }
  },

  /**
   * Resend verification email
   */
  async resendVerification(email) {
    try {
      const response = await api.post('/auth/resend-verification', { email })
      return response.data
    } catch (error) {
      logger.error('❌ Resend verification error:', error)
      throw error
    }
  }
}

/**
 * Trading API Service
 */
export const tradingService = {
  /**
   * Get user's trading dashboard data
   */
  async getDashboardData() {
    try {
      const response = await api.get('/trading/dashboard')
      return response.data
    } catch (error) {
      logger.error('❌ Get dashboard data error:', error)
      throw error
    }
  },

  /**
   * Get trading algorithms
   */
  async getAlgorithms() {
    try {
      const response = await api.get('/trading/algorithms')
      return response.data
    } catch (error) {
      logger.error('❌ Get algorithms error:', error)
      throw error
    }
  },

  /**
   * Get single algorithm by ID
   */
  async getAlgorithm(algorithmId) {
    try {
      const response = await api.get(`/trading/algorithms/${algorithmId}`)
      return response.data
    } catch (error) {
      logger.error('❌ Get algorithm error:', error)
      throw error
    }
  },

  /**
   * Get latest draft algorithm
   */
  async getLatestDraft() {
    try {
      const response = await api.get('/trading/algorithms/drafts/latest')
      return response.data
    } catch {
      return null
    }
  },

  /**
   * Create new algorithm
   */
  async createAlgorithm(algorithmData) {
    try {
      const response = await api.post('/trading/algorithms', algorithmData)
      return response.data
    } catch (error) {
      logger.error('❌ Create algorithm error:', error)
      throw error
    }
  },

  /**
   * Update algorithm
   */
  async updateAlgorithm(algorithmId, algorithmData) {
    try {
      const response = await api.put(
        `/trading/algorithms/${algorithmId}`,
        algorithmData
      )
      return response.data
    } catch (error) {
      logger.error('❌ Update algorithm error:', error)
      throw error
    }
  },

  /**
   * Delete algorithm
   */
  async deleteAlgorithm(algorithmId) {
    try {
      const response = await api.delete(`/trading/algorithms/${algorithmId}`)
      // Return both the response data and the archived flag if present
      return {
        ...response.data,
        archived: response.data.data?.archived || false
      }
    } catch (error) {
      logger.error('❌ Delete algorithm error:', error)
      throw error
    }
  },

  /**
   * Clone algorithm
   */
  async cloneAlgorithm(algorithmId, cloneData) {
    try {
      const response = await api.post(
        `/trading/algorithms/${algorithmId}/clone`,
        cloneData
      )
      return response.data
    } catch (error) {
      logger.error('❌ Clone algorithm error:', error)
      throw error
    }
  },

  /**
   * Complete algorithm - validate all fields and transition from draft to stopped
   */
  async completeAlgorithm(algorithmId) {
    try {
      const response = await api.post(
        `/trading/algorithms/${algorithmId}/complete`
      )
      return response.data
    } catch (error) {
      logger.error('❌ Complete algorithm error:', error)
      throw error
    }
  },

  /**
   * Stop algorithm
   */
  async stopAlgorithm(algorithmId) {
    try {
      const response = await api.post(`/trading/algorithms/${algorithmId}/stop`)
      return response.data
    } catch (error) {
      logger.error('❌ Stop algorithm error:', error)
      throw error
    }
  },

  /**
   * Get trading performance data
   */
  async getPerformanceData(timeRange = '7d') {
    try {
      const response = await api.get(`/trading/performance?range=${timeRange}`)
      return response.data
    } catch (error) {
      logger.error('❌ Get performance data error:', error)
      throw error
    }
  },

  /**
   * Execute trading strategy
   */
  async executeStrategy(strategyId, parameters = {}) {
    try {
      const response = await api.post(
        `/trading/strategies/${strategyId}/execute`,
        parameters
      )
      return response.data
    } catch (error) {
      logger.error('❌ Execute strategy error:', error)
      throw error
    }
  },

  /**
   * Get trading history
   */
  async getTradingHistory(page = 1, limit = 50) {
    try {
      const response = await api.get(
        `/trading/history?page=${page}&limit=${limit}`
      )
      return response.data
    } catch (error) {
      logger.error('❌ Get trading history error:', error)
      throw error
    }
  }
}

/**
 * Admin API Service (for admin users)
 */
export const adminService = {
  /**
   * Get all users (admin only)
   */
  async getUsers(page = 1, limit = 50, filters = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      })

      const response = await api.get(`/admin/users?${queryParams}`)
      return response.data
    } catch (error) {
      logger.error('❌ Get users error:', error)
      throw error
    }
  },

  /**
   * Update user role (admin only)
   */
  async updateUserRole(userId, roles) {
    try {
      const response = await api.put(`/admin/users/${userId}/roles`, { roles })
      return response.data
    } catch (error) {
      logger.error('❌ Update user role error:', error)
      throw error
    }
  },

  /**
   * Get security alerts (admin only)
   */
  async getSecurityAlerts() {
    try {
      const response = await api.get('/admin/security/alerts')
      return response.data
    } catch (error) {
      logger.error('❌ Get security alerts error:', error)
      throw error
    }
  },

  /**
   * Acknowledge security alert (admin only)
   */
  async acknowledgeAlert(alertId) {
    try {
      const response = await api.post(
        `/admin/security/alerts/${alertId}/acknowledge`
      )
      return response.data
    } catch (error) {
      logger.error('❌ Acknowledge alert error:', error)
      throw error
    }
  }
}

/**
 * API helper functions
 */
export const apiHelpers = {
  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('user')
  },

  /**
   * Get stored user data
   */
  getStoredUser() {
    try {
      const user = localStorage.getItem('user')
      return user ? JSON.parse(user) : null
    } catch (error) {
      logger.error('Error parsing stored user:', error)
      localStorage.removeItem('user')
      return null
    }
  },

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    const user = this.getStoredUser()
    return user?.role === role || false
  },

  /**
   * Check if user is admin
   */
  isAdmin() {
    return this.hasRole('admin') || this.hasRole('superAdmin')
  },

  /**
   * Check if user is verified
   */
  isVerified() {
    const user = this.getStoredUser()
    return user?.isVerified || false
  },

  /**
   * Get user's subscription status
   */
  getSubscriptionStatus() {
    const user = this.getStoredUser()
    return user?.subscription?.status || 'inactive'
  },

  /**
   * Check if user has active subscription
   */
  hasActiveSubscription() {
    const user = this.getStoredUser()
    return (
      user?.subscription?.status === 'active' &&
      new Date(user.subscription.currentPeriodEnd) > new Date()
    )
  }
}

/**
 * AI Chat Model API Service
 */
export const aiChatService = {
  /**
   * List available AI models.
   * @param {Object} filters – optional query params (e.g. { supportsStreaming: true })
   */
  async getModels(filters = {}) {
    const params = Object.keys(filters).length > 0 ? filters : undefined
    const response = await api.get('/ai-chat/models', { params })
    return response.data
  },

  /**
   * Get the current user's model preference.
   * @param {string} [conversationId] – fetch conversation-scoped preference when provided
   */
  async getModelPreference(conversationId) {
    const params = conversationId ? { conversationId } : undefined
    const response = await api.get('/ai-chat/models/preference', { params })
    return response.data
  },

  /**
   * Set the user's model preference.
   * @param {string} modelId
   * @param {string} [reasoningEffort] – 'low' | 'medium' | 'high'
   * @param {string} [conversationId]  – scope to a specific conversation when provided
   */
  async setModelPreference(modelId, reasoningEffort, conversationId) {
    const body = {
      modelId,
      ...(reasoningEffort ? { reasoningEffort } : {}),
      ...(conversationId ? { conversationId } : {})
    }
    const response = await api.put('/ai-chat/models/preference', body)
    return response.data
  },

  /**
   * Approve a pending order confirmation.
   * @param {string} confirmId - Confirmation ID from the 'confirm' SSE frame
   */
  async approveOrder(confirmId) {
    return api.post(`/ai-chat/orders/${confirmId}/approve`)
  },

  /**
   * Reject a pending order confirmation.
   * @param {string} confirmId - Confirmation ID from the 'confirm' SSE frame
   * @param {string} [reason]  - Optional rejection reason
   */
  async rejectOrder(confirmId, reason) {
    return api.post(`/ai-chat/orders/${confirmId}/reject`, { reason })
  },

  /**
   * Get all pending order confirmations for the current user.
   */
  async getPendingOrders() {
    return api.get('/ai-chat/orders/pending')
  },

  /**
   * Get the status of a specific order confirmation.
   * @param {string} confirmId - Confirmation ID
   */
  async getOrderStatus(confirmId) {
    return api.get(`/ai-chat/orders/${confirmId}`)
  },

  /**
   * Edit a user message (truncates all messages that follow it).
   * @param {string} conversationId
   * @param {string} messageId
   * @param {string} content - New message content
   */
  async editMessage(conversationId, messageId, content) {
    return api.put(
      `/ai-chat/conversations/${conversationId}/messages/${messageId}`,
      { content }
    )
  },

  /**
   * Get edit history for a message.
   * @param {string} conversationId
   * @param {string} messageId
   */
  async getMessageHistory(conversationId, messageId) {
    return api.get(
      `/ai-chat/conversations/${conversationId}/messages/${messageId}/history`
    )
  },

  /**
   * Retry generating a response by removing messages after the given
   * user message and returning the original prompt content.
   * @param {string} conversationId
   * @param {string} messageId - ID of the user message to retry from
   */
  async retryMessage(conversationId, messageId) {
    return api.post(
      `/ai-chat/conversations/${conversationId}/messages/${messageId}/retry`
    )
  }
}

export default api
